import React from 'react';
import Breadcrumb from '../components/ui/Breadcrumb';
import Icon from '../components/AppIcon';

const Help = () => {
  return (
    <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8">
      <Breadcrumb />
          <div className="flex items-center space-x-3">
            <Icon name="HelpCircle" size={24} className="text-primary sm:hidden" />
            <Icon name="HelpCircle" size={28} className="text-primary hidden sm:block" />
            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Help & Support Center</h1>
          </div>
          <p className="text-muted-foreground text-base sm:text-lg">Comprehensive guide to using InventoryTracker effectively for your business.</p>

          {/* Table of Contents */}
          <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground flex items-center">
              <Icon name="List" size={18} className="text-primary sm:hidden mr-2" />
              <Icon name="List" size={20} className="text-primary hidden sm:block mr-2" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#getting-started" className="text-primary hover:underline">Getting Started</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#dashboard" className="text-primary hover:underline">Dashboard</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#product-catalog" className="text-primary hover:underline">Product Catalog</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#barcode-scanning" className="text-primary hover:underline">Barcode Scanning</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#reports" className="text-primary hover:underline">Reports & Analytics</a>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#activity-log" className="text-primary hover:underline">Activity Log</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#settings" className="text-primary hover:underline">Settings</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#profile" className="text-primary hover:underline">User Profile</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#abbreviations" className="text-primary hover:underline">Abbreviations & Terms</a>
                  </li>
                  <li className="flex items-center">
                    <Icon name="ChevronRight" size={16} className="mr-1 text-primary" />
                    <a href="#troubleshooting" className="text-primary hover:underline">Troubleshooting</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Getting Started */}
          <section id="getting-started" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="Play" size={20} className="mr-2 text-primary" />
              Getting Started
            </h2>
            <p className="text-muted-foreground">Welcome to InventoryTracker! Follow these steps to set up your inventory management system:</p>
            <ol className="list-decimal pl-6 space-y-3 text-sm">
              <li className="text-foreground">
                <span className="font-medium">Create an Account</span> - Register with your business email or sign in if you already have an account.
              </li>
              <li className="text-foreground">
                <span className="font-medium">Set Up Your Profile</span> - Complete your business profile with company details and preferences.
              </li>
              <li className="text-foreground">
                <span className="font-medium">Add Products</span> - Navigate to the Product Catalog and add your inventory items with details like name, SKU, price, and stock levels.
              </li>
              <li className="text-foreground">
                <span className="font-medium">Configure Categories</span> - Organize your products into categories for easier management.
              </li>
              <li className="text-foreground">
                <span className="font-medium">Set Up Suppliers</span> - Add your suppliers to track product sources and manage purchase orders.
              </li>
              <li className="text-foreground">
                <span className="font-medium">Explore Features</span> - Familiarize yourself with barcode scanning, reporting, and other features.
              </li>
            </ol>
            <div className="bg-muted/30 p-4 rounded-md border border-border mt-2">
              <p className="text-sm flex items-start">
                <Icon name="Lightbulb" size={16} className="mr-2 text-amber-500 mt-0.5" />
                <span><strong>Pro Tip:</strong> Use keyboard shortcuts (press <kbd className="px-2 py-0.5 rounded bg-muted border border-border text-xs">?</kbd> to view) for faster navigation and operations.</span>
              </p>
            </div>
          </section>

          {/* Dashboard */}
          <section id="dashboard" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="LayoutDashboard" size={20} className="mr-2 text-primary" />
              Dashboard
            </h2>
            <p className="text-muted-foreground">The Dashboard provides a comprehensive overview of your inventory status and business metrics:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Key Metrics</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Total Inventory Value - The current monetary value of all your stock.</li>
                  <li>Low Stock Items - Products that have fallen below their reorder points.</li>
                  <li>Out of Stock Items - Products with zero inventory that need attention.</li>
                  <li>Recent Activity - Latest transactions and inventory changes.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Quick Actions</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Add Product</strong> (Ctrl+N) - Quickly add a new product to your inventory.</li>
                  <li><strong>Create Purchase Order</strong> (Ctrl+P) - Generate a new purchase order for suppliers.</li>
                  <li><strong>Scan Barcode</strong> (Ctrl+B) - Open the barcode scanner to find or sell products.</li>
                  <li><strong>Generate Report</strong> (Ctrl+R) - Create inventory reports and analytics.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Widgets</h3>
                <p className="text-sm">Dashboard widgets can be customized to show the information most relevant to your business. Click the settings icon on any widget to configure or reposition it.</p>
              </div>
            </div>
          </section>

          {/* Product Catalog */}
          <section id="product-catalog" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="Package" size={20} className="mr-2 text-primary" />
              Product Catalog
            </h2>
            <p className="text-muted-foreground">The Product Catalog is the central hub for managing your inventory items:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Adding Products</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Click the <strong>Add Product</strong> button to open the product creation form.</li>
                  <li>Fill in required fields: Name, SKU (or auto-generate), Category, and Price.</li>
                  <li>Add optional details: Description, Cost, Weight, Dimensions, etc.</li>
                  <li>Upload a product image by clicking the image upload area.</li>
                  <li>Set initial stock levels and reorder points for inventory tracking.</li>
                  <li>Click <strong>Add Product</strong> to save the new item to your catalog.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Managing Products</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Use filters to find products by category, supplier, stock status, etc.</li>
                  <li>Click on a product row to view detailed information and edit options.</li>
                  <li>Use bulk actions to update multiple products simultaneously.</li>
                  <li>Adjust stock levels directly from the product details page.</li>
                  <li>Track product history, including sales and stock adjustments.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Product Fields Explained</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>SKU</strong> (Stock Keeping Unit) - A unique identifier for each product.</li>
                  <li><strong>Barcode</strong> - UPC, EAN, or custom barcode for scanning.</li>
                  <li><strong>Reorder Point</strong> - Stock level that triggers a low stock alert.</li>
                  <li><strong>Active Status</strong> - Determines if a product is available for sale.</li>
                  <li><strong>Track Inventory</strong> - Enable/disable stock level monitoring for this product.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Barcode Scanning */}
          <section id="barcode-scanning" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="QrCode" size={20} className="mr-2 text-primary" />
              Barcode Scanning
            </h2>
            <p className="text-muted-foreground">The barcode scanning feature allows for quick product lookup and inventory operations:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">How to Access the Scanner</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Click the <strong>Scan Barcode</strong> button in the Product Catalog.</li>
                  <li>Use the keyboard shortcut <kbd className="px-2 py-0.5 rounded bg-muted border border-border text-xs">Ctrl+B</kbd> from anywhere in the app.</li>
                  <li>Access from the Quick Actions panel on the Dashboard.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Scanning Process</h3>
                <ol className="list-decimal pl-6 text-sm space-y-1">
                  <li>When the scanner opens, position your device's camera to view the barcode.</li>
                  <li>The system will automatically detect and scan the barcode.</li>
                  <li>If a matching product is found, the product details will be displayed.</li>
                  <li>If no match is found, you'll have the option to add a new product with that barcode.</li>
                </ol>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Barcode Scan Buy Function</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>After scanning a product, click the <strong>Quick Sell</strong> button.</li>
                  <li>Enter the quantity being sold and confirm the transaction.</li>
                  <li>The system will automatically update inventory levels.</li>
                  <li>A transaction record will be created in the Activity Log.</li>
                  <li>For multiple items, use the <strong>Add to Cart</strong> feature before finalizing the sale.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Supported Barcode Types</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>UPC-A</strong> (Universal Product Code) - 12-digit code used primarily in North America.</li>
                  <li><strong>EAN-13</strong> (European Article Number) - 13-digit code used internationally.</li>
                  <li><strong>Code 128</strong> - High-density alphanumeric barcode.</li>
                  <li><strong>QR Codes</strong> - 2D barcodes that can store more information.</li>
                  <li><strong>Custom</strong> - Your own generated barcodes for internal use.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Reports & Analytics */}
          <section id="reports" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
              Reports & Analytics
            </h2>
            <p className="text-muted-foreground">Generate detailed reports and gain insights into your inventory performance:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Available Reports</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Inventory Status</strong> - Current stock levels, values, and status.</li>
                  <li><strong>Sales Analysis</strong> - Sales trends, top-selling products, and revenue.</li>
                  <li><strong>Purchase Orders</strong> - Pending, completed, and historical purchase orders.</li>
                  <li><strong>Low Stock</strong> - Items below reorder points that need replenishment.</li>
                  <li><strong>Product Performance</strong> - Turnover rates, profitability, and sales velocity.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Exporting Data</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Click the <strong>Export</strong> button on any report to download as CSV.</li>
                  <li>Use filters to customize the data included in your export.</li>
                  <li>Schedule regular exports via the Settings page.</li>
                  <li>CSV files can be imported into accounting software or spreadsheets.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">AI-Powered Insights</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Click <strong>Generate Insights</strong> to analyze your inventory data.</li>
                  <li>The AI will identify trends, opportunities, and potential issues.</li>
                  <li>Receive recommendations for inventory optimization.</li>
                  <li>Get forecasting predictions based on historical data.</li>
                  <li>Insights are generated using advanced machine learning algorithms.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Activity Log */}
          <section id="activity-log" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="History" size={20} className="mr-2 text-primary" />
              Activity Log
            </h2>
            <p className="text-muted-foreground">Track all inventory-related activities and transactions:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Tracked Activities</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Product additions, modifications, and deletions.</li>
                  <li>Stock adjustments and inventory counts.</li>
                  <li>Sales transactions and returns.</li>
                  <li>Purchase order creation and fulfillment.</li>
                  <li>User logins and system changes.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Filtering the Log</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Filter by date range to view specific time periods.</li>
                  <li>Filter by activity type to focus on particular operations.</li>
                  <li>Filter by user to track individual team member actions.</li>
                  <li>Filter by product to see the history of a specific item.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Audit Trail</h3>
                <p className="text-sm">The Activity Log serves as an audit trail for compliance and accountability. All entries include timestamps, user information, and detailed descriptions of the actions performed.</p>
              </div>
            </div>
          </section>

          {/* Settings */}
          <section id="settings" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="Settings" size={20} className="mr-2 text-primary" />
              Settings
            </h2>
            <p className="text-muted-foreground">Customize your InventoryTracker experience:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Notification Preferences</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Email Notifications</strong> - Receive alerts for low stock, completed orders, etc.</li>
                  <li><strong>Weekly Summary</strong> - Get a weekly digest of inventory activities and metrics.</li>
                  <li><strong>Alert Thresholds</strong> - Customize when low stock notifications are triggered.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Appearance</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Dark Mode</strong> - Toggle between light and dark interface themes.</li>
                  <li><strong>Density</strong> - Adjust the compactness of information display.</li>
                  <li><strong>Dashboard Layout</strong> - Customize which widgets appear on your dashboard.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Account Settings</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Password</strong> - Change your account password.</li>
                  <li><strong>Two-Factor Authentication</strong> - Enable for additional security.</li>
                  <li><strong>Team Members</strong> - Manage user access and permissions.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* User Profile */}
          <section id="profile" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="User" size={20} className="mr-2 text-primary" />
              User Profile
            </h2>
            <p className="text-muted-foreground">Manage your personal information and preferences:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Profile Information</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>Update your name, email, and contact details.</li>
                  <li>Upload or change your profile picture.</li>
                  <li>Set your preferred language and regional settings.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Activity History</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>View your recent actions and transactions.</li>
                  <li>Track your login history and device access.</li>
                  <li>Monitor your report generation and exports.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Abbreviations & Terms */}
          <section id="abbreviations" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="BookOpen" size={20} className="mr-2 text-primary" />
              Abbreviations & Terms
            </h2>
            <p className="text-muted-foreground">Common terminology used throughout the application:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <ul className="text-sm space-y-2">
                  <li><strong>SKU</strong> - Stock Keeping Unit, a unique identifier for tracking inventory.</li>
                  <li><strong>UPC</strong> - Universal Product Code, a barcode symbology used primarily in North America.</li>
                  <li><strong>EAN</strong> - European Article Number, an international standard barcode format.</li>
                  <li><strong>COGS</strong> - Cost of Goods Sold, the direct costs attributable to the production of goods sold.</li>
                  <li><strong>PO</strong> - Purchase Order, a document sent to suppliers to order products.</li>
                  <li><strong>ROI</strong> - Return on Investment, a performance measure used to evaluate efficiency.</li>
                  <li><strong>FIFO</strong> - First In, First Out, an inventory valuation method.</li>
                  <li><strong>LIFO</strong> - Last In, First Out, an alternative inventory valuation method.</li>
                </ul>
              </div>
              <div>
                <ul className="text-sm space-y-2">
                  <li><strong>MOQ</strong> - Minimum Order Quantity, the smallest amount a supplier will sell.</li>
                  <li><strong>EOQ</strong> - Economic Order Quantity, the ideal order quantity that minimizes costs.</li>
                  <li><strong>QOH</strong> - Quantity on Hand, current available stock.</li>
                  <li><strong>ROP</strong> - Reorder Point, the inventory level that triggers a new order.</li>
                  <li><strong>WMS</strong> - Warehouse Management System, for managing warehouse operations.</li>
                  <li><strong>KPI</strong> - Key Performance Indicator, measurable values that show effectiveness.</li>
                  <li><strong>B2B</strong> - Business to Business, commerce transactions between businesses.</li>
                  <li><strong>B2C</strong> - Business to Consumer, commerce transactions with end customers.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <Icon name="AlertCircle" size={20} className="mr-2 text-primary" />
              Troubleshooting
            </h2>
            <p className="text-muted-foreground">Solutions for common issues:</p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Barcode Scanner Issues</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Scanner not detecting barcodes</strong> - Ensure adequate lighting and proper camera focus.</li>
                  <li><strong>Unrecognized barcodes</strong> - Verify the barcode type is supported and properly formatted.</li>
                  <li><strong>Camera access denied</strong> - Check browser permissions and allow camera access.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Data Import/Export Problems</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>CSV import errors</strong> - Ensure your file follows the required format and column headers.</li>
                  <li><strong>Export not downloading</strong> - Check browser download settings and try a different browser.</li>
                  <li><strong>Missing data in exports</strong> - Verify that your filters aren't excluding the desired information.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-1">Account Access</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><strong>Forgotten password</strong> - Use the "Forgot Password" link on the login page.</li>
                  <li><strong>Account locked</strong> - Contact your administrator or support after multiple failed login attempts.</li>
                  <li><strong>Session timeout</strong> - Adjust session timeout settings or log back in.</li>
                </ul>
              </div>
              <div className="bg-muted/30 p-4 rounded-md border border-border mt-2">
                <p className="text-sm flex items-start">
                  <Icon name="HelpCircle" size={16} className="mr-2 text-primary mt-0.5" />
                  <span>For additional support, contact our help desk at <a href="mailto:support@inventorytracker.com" className="text-primary hover:underline">support@inventorytracker.com</a> or use the in-app chat support feature.</span>
                </p>
              </div>
            </div>
          </section>
        </div>
  );
};

export default Help;
